// documentTitle — what the browser tab says.
//
// index.html shipped a hardcoded `<title>AvatarOS - Myra</title>`, so every
// character in the catalog showed up as "Myra" — including the launcher, where no
// character is open at all. A tab title naming the wrong document is worse than a
// generic one: with several editor tabs open it is the ONLY thing distinguishing
// them, and a wrong one sends you to the wrong tab.
//
// Pure string building, no DOM: the caller owns `document.title`, so the format is
// testable without a browser.

/** The product name, and the tab title on its own when nothing is open. */
export const APP_NAME = 'AvatarOS'

/**
 * Build a tab title.
 *
 * Order is document-first — "Myra — AvatarOS", not "AvatarOS — Myra" — because a
 * tab strip truncates from the RIGHT. With the app name first, ten open tabs all
 * read "AvatarOS…" and none of them tell you which character they hold.
 *
 * @param {object} [opts]
 * @param {string|null} [opts.characterLabel] display name of the open character,
 *   or null/empty on the launcher, during boot, and on the empty state.
 * @param {string|null} [opts.surface] full-viewport editor currently open
 *   ('Body graph', 'Face graph', 'Event graph'), or null for the scene.
 * @param {boolean} [opts.dirty] unsaved changes — marked with a leading dot, the
 *   convention every desktop editor uses. Deliberately a dot and not the word
 *   "unsaved": it survives truncation to a few characters.
 * @returns {string}
 */
export function formatDocumentTitle({ characterLabel = null, surface = null, dirty = false } = {}) {
  const name = typeof characterLabel === 'string' ? characterLabel.trim() : ''
  if (!name) {
    // No character open. The app name alone — never a stale character, and never
    // a "Loading…" that outlives the load and becomes a lie.
    return APP_NAME
  }
  const where = typeof surface === 'string' && surface.trim() ? ` · ${surface.trim()}` : ''
  return `${dirty ? '• ' : ''}${name}${where} — ${APP_NAME}`
}

export default formatDocumentTitle
